import { MEDIA_QUERY } from '@bucketplace/design-system';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  DehydratedState,
  dehydrate,
  QueryClient,
  useQuery,
} from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { GetStaticPropsResult, NextPage } from 'next';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { EndModal } from '@/components/EndModal';
import { Error } from '@/components/Error';
import FeedHead from '@/components/Layout/FeedHead';
import { createGetBigPromotionQueryOptions } from '@/promotions/api/get-big-promotion/get-big-promotion';
import { getBigPromotion as getBigPromotionClient } from '@/promotions/api/get-big-promotion/get-big-promotion.client';
import { getBigPromotion as getBigPromotionServer } from '@/promotions/api/get-big-promotion/get-big-promotion.server';
import { createGetExhibitionQueryOptions } from '@/promotions/api/get-exhibition/get-exhibition';
import { getExhibition as getExhibitionClient } from '@/promotions/api/get-exhibition/get-exhibition.client';
import { getExhibition as getExhibitionServer } from '@/promotions/api/get-exhibition/get-exhibition.server';
import { PromotionsTemplate } from '@/promotions/components/PromotionsTemplate';
import { usePromotionQuery } from '@/promotions/hooks/usePromotionQuery';

const isProd = process.env.APP_PROFILE === 'prod';

const redirectSourceType = ['INTERNAL_LINK', 'SOURCE_TYPE_UNSPECIFIED'];

const shouldRedirect = ({
  sourceType,
  status,
}: {
  sourceType?: string;
  status?: string;
} = {}): boolean => {
  if (status !== 'IN_PROGRESS') {
    return false;
  }

  return redirectSourceType.includes(sourceType as any);
};

const defaultMeta = {
  title: '오늘의집 집요한세일',
  description: '집에 필요한. 탁월한 세일.',
  imageUrl:
    'https://prs.ohousecdn.com/apne2/commerce/promotion/v1-476937909534784.png',
};

export interface BigPromotionPageProps {
  readonly dehydratedState?: DehydratedState;
  readonly statusCode?: number;
}

const BigPromotionPage: NextPage<BigPromotionPageProps> = (props) => {
  const [isEditorLoaded, setIsEditorLoaded] = useState(false);
  const router = useRouter();

  const { data: bigPromotion, isLoading: isBigPromotionLoading } = useQuery(
    createGetBigPromotionQueryOptions({
      api: getBigPromotionClient,
    }),
  );
  const { promotionId, anchorSection, anchorTargetId } = usePromotionQuery({
    isProd,
    defaultId: bigPromotion?.promotion?.sourceId,
  });
  const { data: promotionData, error: promotionError } = useQuery(
    createGetExhibitionQueryOptions({
      api: getExhibitionClient,
      input: { id: promotionId },
      skip: isBigPromotionLoading,
    }),
  );

  const isLoading = (!promotionData && !promotionError) || !isEditorLoaded;
  const isInitialAnchor = anchorSection === 'true';
  const statusCode =
    promotionData?.statusCode ??
    ('statusCode' in props ? props.statusCode : undefined);

  const metadata = {
    title: bigPromotion?.promotion?.sharingMetadata?.title ?? defaultMeta.title,
    description:
      bigPromotion?.promotion?.sharingMetadata?.subtitle ??
      defaultMeta.description,
    ogTitle:
      bigPromotion?.promotion?.sharingMetadata?.title ?? defaultMeta.title,
    ogDescription:
      bigPromotion?.promotion?.sharingMetadata?.subtitle ??
      defaultMeta.description,
    ogType: 'website',
    image:
      bigPromotion?.promotion?.sharingMetadata?.imageUrl ??
      defaultMeta.imageUrl,
  };

  useEffect(() => {
    if (
      shouldRedirect({
        sourceType: bigPromotion?.promotion?.sourceType,
        status: bigPromotion?.promotion?.status,
      }) &&
      bigPromotion?.promotion?.landingUrl
    ) {
      window.location.replace(bigPromotion.promotion.landingUrl);
    }
  }, [
    bigPromotion?.promotion?.landingUrl,
    bigPromotion?.promotion?.sourceType,
    bigPromotion?.promotion?.status,
    router,
  ]);

  // 내부 링크인 경우 헤드만 렌더링
  if (shouldRedirect(bigPromotion?.promotion)) {
    return <FeedHead {...metadata} />;
  }

  const showTemplate = router.isReady && promotionData?.exhibition;
  const showError =
    !showTemplate &&
    bigPromotion?.promotion?.status !== 'CLOSED' &&
    statusCode &&
    statusCode >= 400;
  const showEndModal =
    router.isReady &&
    bigPromotion?.promotion?.status !== 'IN_PROGRESS' &&
    !showError;

  return (
    <>
      <FeedHead {...metadata} />
      {showError && <Error statusCode={statusCode} />}

      {showTemplate && (
        <Template
          units={promotionData.exhibition!.details[0].units}
          promotionId={promotionData.exhibition!.id}
          title={promotionData.exhibition!.title}
          coverImageUrls={promotionData.exhibition!.coverImageUrls}
          detailId={promotionData.exhibition!.details[0].id}
          anchorInfo={{
            targetId: anchorTargetId,
            isInitialAnchor,
          }}
          isLoading={isLoading}
          onViewerLoaded={(isLoaded: boolean) => {
            setIsEditorLoaded(isLoaded);
          }}
        />
      )}

      {showEndModal && (
        <EndModal
          title="프로모션 종료"
          contents="현재 진행중인 프로모션이 없습니다."
          end={0}
          redirectTarget="store"
        />
      )}
    </>
  );
};

export const getStaticProps = async (): Promise<
  GetStaticPropsResult<BigPromotionPageProps>
> => {
  const queryClient = new QueryClient();
  const errorResponse = {
    props: {},
    revalidate: 1,
  };

  const isBuildPhase = process.env.NEXT_PHASE === 'phase-production-build';
  if (isBuildPhase) {
    return errorResponse;
  }

  try {
    const bigPromotion = await queryClient.fetchQuery(
      createGetBigPromotionQueryOptions({
        api: getBigPromotionServer,
      }),
    );

    if (shouldRedirect(bigPromotion.promotion)) {
      return {
        redirect: {
          permanent: false,
          destination: bigPromotion!.promotion!.landingUrl,
        },
        revalidate: 1,
      };
    }

    await queryClient.prefetchQuery(
      createGetExhibitionQueryOptions({
        api: getExhibitionServer,
        input: { id: bigPromotion.promotion?.sourceId ?? 0 },
      }),
    );

    return {
      props: {
        dehydratedState: dehydrate(queryClient),
      },
      revalidate: 1,
    };
  } catch (err) {
    if (err instanceof HTTPError) {
      return {
        ...errorResponse,
        props: {
          ...errorResponse.props,
          dehydratedState: dehydrate(queryClient),
          statusCode: err.response.status,
        },
      };
    }

    return {
      ...errorResponse,
      props: {
        ...errorResponse.props,
        dehydratedState: dehydrate(queryClient),
      },
    };
  }
};

const Template = styled(PromotionsTemplate)`
  padding: 0 60px;
  ${MEDIA_QUERY.max(
    'tablet',
    css`
    padding: 0;
  `,
  )}
`;

export default BigPromotionPage;
